import { useEffect, useRef } from 'react';

import { useCurrenciesQuery } from '@/api/config';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import { reduxFatalitySlice } from '@/store/reduxFatality/slice.ts';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1_000;

export const useLoadedCurrencies = (): boolean => {
  const dispatch = useAppDispatch();
  const { isSuccess, isError, isFetching, refetch } = useCurrenciesQuery();
  const dispatchRef = useRef(dispatch);
  const refetchRef = useRef(refetch);

  dispatchRef.current = dispatch;
  refetchRef.current = refetch;

  const isRetryingRef = useRef(false);

  useEffect(() => {
    if (!isError || isFetching) {
      return;
    }

    if (isRetryingRef.current) {
      return;
    }

    isRetryingRef.current = true;

    const performRetries = async () => {
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        logger.logWarning('Currencies load failed, retrying ({attempt_n}/{maxRetries_n})', attempt, MAX_RETRIES);

        await new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

        const result = await refetchRef.current();

        if (!result.isError) {
          isRetryingRef.current = false;
          return;
        }
      }

      logger.logError('Currencies failed to load after {maxRetries_n} retries — crashing app', MAX_RETRIES);
      const fatalError = new Error(`Failed to load currencies after ${MAX_RETRIES} retries`);

      dispatchRef.current(
        reduxFatalitySlice.actions.setError({
          errorMessage: fatalError.message,
          errorStack: fatalError.stack,
          action: { type: 'currencies/loadFailed' },
        }),
      );

      isRetryingRef.current = false;
    };

    performRetries();
  }, [isError, isFetching]);

  return isSuccess;
};
